export const ALERT_LANES = [
  { key: 'lms', label: 'LMS', color: '#9CC3F5', components: ['lms'] },
  { key: 'nms', label: 'NMS', color: '#F4CD7A', components: ['network_manager', 'nms'] },
  { key: 'data', label: 'Data', color: '#B7A6E3', components: ['pubsub', 'dataflow', 'dataform', 'edge_devices'] },
];

const OTHER_LANE = { key: 'other', label: 'Other', color: '#C4C4CC', components: [] };

export function eventTimeMs(value) {
  if (!value) return null;
  const seconds = value._seconds ?? value.seconds;
  if (typeof seconds === 'number') return seconds * 1000;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.getTime();
}

export function bucketCountFor(spanMs) {
  if (spanMs <= 60 * 60 * 1000) return 120;
  if (spanMs <= 6 * 60 * 60 * 1000) return 144;
  if (spanMs <= 48 * 60 * 60 * 1000) return 144;
  if (spanMs <= 14 * 24 * 60 * 60 * 1000) return 168;
  return 120;
}

function laneKeyFor(component) {
  const key = String(component || '').toLowerCase();
  const lane = ALERT_LANES.find((item) => item.components.includes(key));
  return lane ? lane.key : OTHER_LANE.key;
}

// Open alerts run from when they started to the end of the selected window.
// Closed alerts run from when they started until they ended.
function spanFor(alert, windowStart, windowEnd) {
  const started = eventTimeMs(alert.startedAt) ?? eventTimeMs(alert.receivedAt);
  if (started === null) return null;
  const ended = eventTimeMs(alert.endedAt);
  const state = String(alert.state || '').toLowerCase();
  const open = state === 'open' || (state !== 'closed' && ended === null);
  const rawEnd = open ? windowEnd : (ended ?? started);
  let start = Math.max(windowStart, started);
  let end = Math.min(windowEnd, rawEnd);
  if (end <= windowStart || start >= windowEnd) return null;
  if (end <= start) end = Math.min(windowEnd, start + 1);
  if (end <= start) return null;
  return { start, end, open, alert };
}

// Stack overlapping ranges onto separate rows so each open-to-closed interval stays visible.
function packSpans(spans) {
  const rowEnds = [];
  const packed = [...spans]
    .sort((a, b) => a.start - b.start || a.end - b.end)
    .map((span) => {
      let row = rowEnds.findIndex((rowEnd) => rowEnd <= span.start);
      if (row < 0) {
        row = rowEnds.length;
        rowEnds.push(span.end);
      } else {
        rowEnds[row] = span.end;
      }
      return { ...span, row };
    });
  return { packed, rows: Math.max(1, rowEnds.length) };
}

export function buildAlertSeries(alerts, windowStart, windowEnd) {
  const spanMs = windowEnd - windowStart;
  if (!(spanMs > 0)) {
    return { lanes: [], max: 0, bucketCount: 0, bucketMs: 0 };
  }
  const bucketCount = bucketCountFor(spanMs);
  const bucketMs = spanMs / bucketCount;
  const definitions = [...ALERT_LANES];
  const grouped = { lms: [], nms: [], data: [], other: [] };
  (alerts || []).forEach((alert) => {
    const span = spanFor(alert, windowStart, windowEnd);
    if (!span) return;
    grouped[laneKeyFor(alert.component)].push(span);
  });
  if (grouped.other.length > 0) definitions.push(OTHER_LANE);

  let max = 0;
  const lanes = definitions.map((definition) => {
    const { packed, rows } = packSpans(grouped[definition.key]);
    const openCounts = new Array(bucketCount).fill(0);
    const closedCounts = new Array(bucketCount).fill(0);
    packed.forEach((span) => {
      const counts = span.open ? openCounts : closedCounts;
      const first = Math.max(0, Math.floor((span.start - windowStart) / bucketMs));
      const last = Math.min(bucketCount - 1, Math.floor((span.end - 1 - windowStart) / bucketMs));
      for (let i = first; i <= last; i += 1) counts[i] += 1;
    });
    max = Math.max(max, ...openCounts.map((count, index) => count + closedCounts[index]));
    return {
      ...definition,
      total: packed.length,
      open: packed.filter((span) => span.open).length,
      closed: packed.filter((span) => !span.open).length,
      rows,
      openCounts,
      closedCounts,
      spans: packed,
    };
  });
  return { lanes, max, bucketCount, bucketMs };
}

export function alertsInBucket(spans, windowStart, bucketMs, index) {
  const bucketStart = windowStart + index * bucketMs;
  const bucketEnd = bucketStart + bucketMs;
  return spans.filter((span) => span.start < bucketEnd && span.end > bucketStart);
}
